// Isolated landing-page motion. No application bundle, editor, or third-party runtime.
export class InvitationLanding {
  els = {};
  timers = new Map();
  frames = new Map();
  anims = new Set();
  obs = [];
  tok = 0;
  split = 40;
  shown = 230;
  touched = false;

  static POS = [
    { x: -72, y: 9, r: -13, s: .78, z: 1 },
    { x: -40, y: 4, r: -6.5, s: .88, z: 3 },
    { x: 0, y: 0, r: 0, s: 1, z: 5 },
    { x: 40, y: 4, r: 6.5, s: .88, z: 3 },
    { x: 72, y: 9, r: 13, s: .78, z: 1 }
  ];
  static KIDS = [
    { n: 'Maya', a: 9, bg: '#0C659E', fg: '#FFFFFF', gift: 'Lego city set', ch: 'Make-A-Wish Canada' },
    { n: 'Leo', a: 8, bg: '#F59FB7', fg: '#0A2A40', gift: 'Hockey skates', ch: 'SickKids Foundation' },
    { n: 'Zara', a: 11, bg: '#F2BA00', fg: '#0A2A40', gift: 'Headphones', ch: 'Right To Play' }
  ];
  static PROMPTS = [
    { p: 'A dinosaur party for a 6-year-old with bright greens and a volcano', v: 0 },
    { p: 'A soccer birthday with a big number 8 and gold stars', v: 2 },
    { p: 'A soft pink party with balloons and a little sparkle', v: 4 }
  ];
  static HERO_SPLITS = [40, 25, 60, 80, 50, 20, 70];
  static DEMO_SPLITS = [40, 70, 25, 55];

  constructor(element) {
    this.element = element;
    element.querySelectorAll('[data-invitation-ref]').forEach(el => { this.els[el.dataset.invitationRef] = el; });
    this.events = new AbortController();
    const signal = this.events.signal;
    const bind = (el, name, fn) => el?.addEventListener(name, fn, { signal });
    bind(this.els.bar, 'pointerdown', this.onBarDown);
    bind(this.els.bar, 'pointermove', this.onBarMove);
    bind(this.els.bar, 'pointerup', this.onBarUp);
    bind(this.els.bar, 'pointercancel', this.onBarUp);
    bind(this.els.bar, 'lostpointercapture', this.onBarUp);
    bind(this.els.knob, 'keydown', this.onKnobKey);
    bind(this.els.knob, 'focus', () => this.touch());
    this.presets = [...element.querySelectorAll('[data-split-preset]')];
    this.presets.forEach(button => bind(button, 'click', () => this.preset(Number(button.dataset.splitPreset))));
    this.motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    bind(this.motionQuery, 'change', () => this.restart());
    bind(document, 'visibilitychange', () => { if (document.hidden) this.stop(); else this.start(); });
    bind(this.els.motionToggle, 'click', () => { this.paused = !this.reduced; this.restart(); });
    if (this.els.motionToggle) this.els.motionToggle.hidden = false;
    this.start();
  }

  restart() { this.stop(); if (!document.hidden) this.start(); }
  destroy() { this.stop(); this.events.abort(); }

  stop() {
    this.tok++;
    this.dragging = false;
    this.timers.forEach((resolve, id) => { clearTimeout(id); resolve(); }); this.timers.clear();
    this.frames.forEach((resolve, id) => { cancelAnimationFrame(id); resolve(); }); this.frames.clear();
    this.obs.forEach(o => o.disconnect()); this.obs = [];
    this.anims.forEach(a => a.cancel()); this.anims.clear();
    if (this._onResize) { window.removeEventListener('resize', this._onResize); this._onResize = null; }
    if (this._selRz) { window.removeEventListener('resize', this._selRz); this._selRz = null; }
    this.caretOn = false;
  }

  wait(ms) {
    return new Promise(resolve => {
      const id = setTimeout(() => { this.timers.delete(id); resolve(); }, ms);
      this.timers.set(id, resolve);
    });
  }
  frame(fn, cancel = () => {}) {
    const id = requestAnimationFrame(now => { this.frames.delete(id); fn(now); });
    this.frames.set(id, cancel);
  }
  alive(t) { return t === this.tok; }
  anim(el, kf, opt) {
    if (this.reduced || !el?.animate) return null;
    const animation = el.animate(kf, opt);
    this.anims.add(animation);
    animation.finished.then(() => this.anims.delete(animation), () => this.anims.delete(animation));
    return animation;
  }
  setText(el, s) {
    if (!el) return;
    const n = el.firstChild;
    if (n && n.nodeType === 3 && el.childNodes.length === 1) n.nodeValue = s; else el.textContent = s;
  }
  tween(from, to, dur, fn, t) {
    return new Promise(res => {
      const t0 = performance.now();
      const f = now => {
        if (!this.alive(t)) { res(); return; }
        const p = Math.min(1, (now - t0) / dur), e = p < .5 ? 4 * p * p * p : 1 - Math.pow(-2 * p + 2, 3) / 2;
        fn(from + (to - from) * e);
        if (p < 1) this.frame(f, res); else res();
      };
      this.frame(f, res);
    });
  }
  count(el, from, to, dur, t) { return this.tween(from, to, dur, v => this.setText(el, '$' + Math.round(v)), t); }
  pulse(el) { this.anim(el, [{ transform: 'scale(1)' }, { transform: 'scale(1.06)' }, { transform: 'scale(1)' }], { duration: 480, easing: 'cubic-bezier(.34,1.56,.64,1)' }); }
  tf(p) { return 'translate(-50%,-50%) translate(' + p.x + '%,' + p.y + '%) rotate(' + p.r + 'deg) scale(' + p.s + ')'; }

  whenVisible(k, fn) {
    const el = this.els[k];
    if (!el) return;
    if (!window.IntersectionObserver) { fn(); return; }
    const o = new IntersectionObserver(es => { if (es.some(e => e.isIntersecting)) { o.disconnect(); fn(); } }, { threshold: .2 });
    o.observe(el); this.obs.push(o);
  }

  start() {
    const t = this.tok;
    this.reduced = this.paused || this.motionQuery.matches;
    this.element.dataset.reducedMotion = String(this.reduced);
    this.setText(this.els.motionToggle, this.reduced ? 'Animations paused' : 'Pause animations');
    if (this.els.motionToggle) {
      this.els.motionToggle.setAttribute('aria-pressed', String(this.reduced));
      this.els.motionToggle.disabled = this.motionQuery.matches;
      if (this.paused && !this.motionQuery.matches) this.setText(this.els.motionToggle, 'Play animations');
    }
    this.restoreDemo();
    this.heroInit(t);
    this.stickyInit(t);
    this.shown = 230; this.applySplit(this.split);
    this.applyKid(InvitationLanding.KIDS[0], 0);
    this.frame(() => { if (this.alive(t)) this.selectLayer('name'); });
    this._selRz = () => { if (this.curLayer) this.selectLayer(this.curLayer); };
    window.addEventListener('resize', this._selRz);
    if (this.reduced) return;
    this.heroLoop(t);
    this.toastLoop(t);
    this.pillLoop(t);
    this.ambient();
    this.whenVisible('splitWrap', () => this.splitLoop(t));
    this.whenVisible('editorWrap', () => { this.typeLoop(t); this.feedLoop(t); });
    this.whenVisible('aiPanel', () => this.aiLoop(t));
  }

  heroInit(t) {
    const P = InvitationLanding.POS, E = this.els;
    this.order = [0, 1, 2, 3, 4];
    this.heroRaisedV = 240; this.k = 0;
    this.applyHeroSplit(40);
    for (let i = 0; i < 5; i++) {
      const el = E['card' + i]; if (!el) continue;
      el.style.transform = this.tf(P[i]); el.style.zIndex = P[i].z;
      const f = E['flip' + i]; if (f) f.style.transform = 'perspective(1400px) rotateY(0deg)';
      if (!this.reduced) this.anim(el, [{ transform: 'translate(-50%,-50%) translate(0%,30%) rotate(0deg) scale(.5)', opacity: 0 }, { transform: this.tf(P[i]), opacity: 1 }], { duration: 1000, delay: 150 + Math.abs(i - 2) * 130, easing: 'cubic-bezier(.34,1.45,.64,1)', fill: 'backwards' });
    }
    for (let i = 0; i < 4; i++) { const el = E['toast' + i]; if (el) { el.style.opacity = i === 0 ? 1 : 0; el.style.transform = 'none'; } }
    if (!this.reduced) {
      this.anim(E.h1, [{ opacity: 0, transform: 'translateY(18px)' }, { opacity: 1, transform: 'none' }], { duration: 800, delay: 100, easing: 'cubic-bezier(.2,.8,.2,1)', fill: 'backwards' });
      [E.pG, E.pC].forEach((el, j) => this.anim(el, [{ transform: 'scale(0) rotate(-12deg)' }, { transform: 'none' }], { duration: 650, delay: 650 + j * 180, easing: 'cubic-bezier(.34,1.56,.64,1)', fill: 'backwards' }));
      this.anim(E.toast0, [{ opacity: 0, transform: 'translateY(14px) scale(.9)' }, { opacity: 1, transform: 'none' }], { duration: 520, delay: 1100, easing: 'cubic-bezier(.34,1.56,.64,1)', fill: 'backwards' });
    }
  }

  applyHeroSplit(c) {
    const E = this.els, cr = Math.round(c), gr = 100 - cr, g = 100 - c;
    this.setText(E.pG, gr + '%'); this.setText(E.pC, cr + '%');
    this.setText(E.mG, gr + '%'); this.setText(E.mC, cr + '%');
    if (E.mBarG) E.mBarG.style.width = 'calc(' + g + '% - 2px)';
    if (E.mKnob) E.mKnob.style.left = g + '%';
  }

  async pillLoop(t) {
    const S = InvitationLanding.HERO_SPLITS;
    let i = 0, cur = S[0];
    await this.wait(2600);
    while (this.alive(t)) {
      i = (i + 1) % S.length;
      const nx = S[i];
      await this.tween(cur, nx, 950, v => this.applyHeroSplit(v), t); if (!this.alive(t)) return;
      cur = nx;
      this.pulse(this.els.pG); this.pulse(this.els.pC);
      await this.wait(2200);
    }
  }

  async heroLoop(t) {
    const P = InvitationLanding.POS, E = this.els, order = this.order;
    const flipOn = true;
    await this.wait(1700);
    while (this.alive(t)) {
      const c = order[2];
      if (flipOn) {
        await this.flip(c, true); if (!this.alive(t)) return;
        await this.wait(2600); if (!this.alive(t)) return;
        await this.flip(c, false); if (!this.alive(t)) return;
        await this.wait(450);
      } else await this.wait(1800);
      if (!this.alive(t)) return;
      const first = order.shift(); order.push(first);
      order.forEach((ci, i) => {
        const el = E['card' + ci]; if (!el) return;
        const from = el.style.transform, to = this.tf(P[i]);
        if (ci === first) {
          el.style.zIndex = 0;
          this.anim(el, [{ transform: from, opacity: 1 }, { transform: 'translate(-50%,-50%) translate(0%,18%) rotate(0deg) scale(.6)', opacity: .3, offset: .5 }, { transform: to, opacity: 1 }], { duration: 950, easing: 'cubic-bezier(.65,0,.35,1)' });
          this.wait(950).then(() => { if (this.alive(t)) el.style.zIndex = P[i].z; });
        } else {
          el.style.zIndex = P[i].z;
          this.anim(el, [{ transform: from }, { transform: to }], { duration: 850, easing: 'cubic-bezier(.65,0,.35,1)' });
        }
        el.style.transform = to;
      });
      this.bumpRaised(t);
      await this.wait(1300);
    }
  }

  flip(ci, toBack) {
    const el = this.els['flip' + ci];
    if (!el) return Promise.resolve();
    const a = 'perspective(1400px) rotateY(0deg)', b = 'perspective(1400px) rotateY(180deg)';
    el.style.transform = toBack ? b : a;
    this.anim(el, [{ transform: toBack ? a : b }, { transform: toBack ? b : a }], { duration: 800, easing: 'cubic-bezier(.45,0,.25,1)' });
    return this.wait(800);
  }

  bumpRaised(t) {
    const adds = [30, 45, 25, 50, 35];
    const from = this.heroRaisedV;
    let to = from + adds[this.k++ % adds.length];
    if (to > 420) to = 150;
    this.heroRaisedV = to;
    this.count(this.els.heroRaised, from, to, 900, t);
    const b = this.els.heroBar;
    if (b) { b.style.transition = 'width .9s cubic-bezier(.65,0,.35,1)'; b.style.width = (to / 420 * 100).toFixed(1) + '%'; }
  }

  async toastLoop(t) {
    let cur = 0;
    await this.wait(4200);
    while (this.alive(t)) {
      const a = this.els['toast' + cur], nx = (cur + 1) % 4, b = this.els['toast' + nx];
      if (a) { a.style.opacity = 0; this.anim(a, [{ opacity: 1, transform: 'none' }, { opacity: 0, transform: 'translateY(-10px) scale(.96)' }], { duration: 280, easing: 'ease-in' }); }
      await this.wait(220); if (!this.alive(t)) return;
      if (b) { b.style.opacity = 1; b.style.transform = 'none'; this.anim(b, [{ opacity: 0, transform: 'translateY(14px) scale(.9)' }, { opacity: 1, transform: 'none' }], { duration: 520, easing: 'cubic-bezier(.34,1.56,.64,1)' }); }
      cur = nx;
      await this.wait(2800);
    }
  }

  ambient() {
    const E = this.els;
    [['dot0', -14, 3600], ['dot1', 12, 4200], ['dot2', -9, 3000]].forEach(([k, dy, d]) => this.anim(E[k], [{ transform: 'translateY(0)' }, { transform: 'translateY(' + dy + 'px)' }], { duration: d, iterations: Infinity, direction: 'alternate', easing: 'ease-in-out' }));
    this.anim(E.ctaArrow, [{ transform: 'translateX(0)' }, { transform: 'translateX(0)', offset: .7 }, { transform: 'translateX(26px)', offset: .82 }, { transform: 'translateX(-26px)', offset: .821 }, { transform: 'translateX(0)' }], { duration: 3200, iterations: Infinity, easing: 'ease-in-out' });
    const blink = [{ opacity: 1, offset: 0 }, { opacity: 1, offset: .5 }, { opacity: 0, offset: .5 }, { opacity: 0, offset: 1 }];
    this.anim(E.caret, blink, { duration: 1000, iterations: Infinity });
    this.anim(E.aiCaret, blink, { duration: 1000, iterations: Infinity });
    this.anim(E.marquee, [{ transform: 'translateX(0)' }, { transform: 'translateX(-50%)' }], { duration: 46000, iterations: Infinity, easing: 'linear' });
    if (!this.touched) this.anim(E.knobRing, [{ transform: 'scale(.8)', opacity: .9 }, { transform: 'scale(1.7)', opacity: 0 }], { duration: 1600, iterations: Infinity, easing: 'ease-out' });
  }

  renderAmounts() {
    const E = this.els, tot = Math.round(this.shown || 0), g = Math.round(tot * (100 - this.split) / 100);
    this.setText(E.gAmt, '$' + g); this.setText(E.cAmt, '$' + (tot - g)); this.setText(E.sTotal, '$' + tot);
  }

  applySplit(c) {
    c = Math.max(20, Math.min(80, c));
    this.split = c;
    this.presets.forEach(button => button.setAttribute('aria-pressed', String(Math.abs(Number(button.dataset.splitPreset) - c) < 0.01)));
    const E = this.els, g = 100 - c, cr = Math.round(c), gr = 100 - cr;
    if (E.bG) E.bG.style.width = g + '%';
    if (E.handle) E.handle.style.left = g + '%';
    this.setText(E.bGp, gr + '%'); this.setText(E.bCp, cr + '%');
    this.setText(E.dG, gr + '%'); this.setText(E.dC, cr + '%');
    if (E.knob) { E.knob.setAttribute('aria-valuenow', cr); E.knob.setAttribute('aria-valuetext', cr + '% to charity, ' + gr + '% to the group gift'); }
    const gx = (3 * g).toFixed(1), cx = (6 * g + 3 * c).toFixed(1);
    const dG = 'M300 50 C300 100 ' + gx + ' 88 ' + gx + ' 140', dC = 'M300 50 C300 100 ' + cx + ' 88 ' + cx + ' 140';
    [['bGl', dG, g], ['hG', dG, g], ['bCl', dC, c], ['hC', dC, c]].forEach(([k, d, s]) => { const p = E[k]; if (p) { p.setAttribute('d', d); p.style.strokeWidth = (3 + 16 * s / 100).toFixed(2); } });
    if (E.coinG) E.coinG.setAttribute('r', (6 + 9 * g / 100).toFixed(1));
    if (E.coinC) E.coinC.setAttribute('r', (6 + 9 * c / 100).toFixed(1));
    this.renderAmounts();
  }

  touch() {
    this.controlRevision = (this.controlRevision || 0) + 1;
    if (this.touched) return;
    this.touched = true;
    const h = this.els.knobRing; if (h) h.style.display = 'none';
  }
  dragAt(e) {
    const b = this.els.bar; if (!b) return;
    const r = b.getBoundingClientRect();
    this.applySplit(Math.round(100 - (e.clientX - r.left) / r.width * 100));
  }
  onBarDown = e => {
    if (!e.isPrimary || e.button !== 0) return;
    this.els.knob?.focus({ preventScroll: true });
    const b = this.els.bar; if (!b) return;
    this.touch(); this.dragging = true;
    try { b.setPointerCapture(e.pointerId); } catch (_) {}
    this.dragAt(e);
  };
  onBarMove = e => { if (this.dragging) this.dragAt(e); };
  onBarUp = () => { this.dragging = false; };
  onKnobKey = e => {
    const step = e.shiftKey ? 10 : 5;
    let c = this.split;
    if (e.key === 'ArrowRight' || e.key === 'ArrowUp') c += step;
    else if (e.key === 'ArrowLeft' || e.key === 'ArrowDown') c -= step;
    else if (e.key === 'Home') c = 20;
    else if (e.key === 'End') c = 80;
    else return;
    e.preventDefault(); this.touch(); this.applySplit(Math.round(c));
  };
  preset(value) {
    this.touch();
    if (this.reduced) { this.applySplit(value); return; }
    const revision = this.controlRevision;
    this.tween(this.split, value, 650, x => { if (revision === this.controlRevision) this.applySplit(x); }, this.tok);
  }

  travel(c, path, dur, t) {
    return new Promise(res => {
      if (!c || !path || !path.getTotalLength) { res(); return; }
      const t0 = performance.now();
      const f = now => {
        if (!this.alive(t)) { res(); return; }
        const p = Math.min(1, (now - t0) / dur), e = p < .5 ? 2 * p * p : 1 - Math.pow(-2 * p + 2, 2) / 2;
        const L = path.getTotalLength(), pt = path.getPointAtLength(L * e);
        c.setAttribute('cx', pt.x); c.setAttribute('cy', pt.y);
        if (p < 1) this.frame(f, res); else res();
      };
      this.frame(f, res);
    });
  }
  flow(h, dur) {
    if (!h || !h.getTotalLength) return;
    const L = h.getTotalLength() + 2;
    h.style.strokeDasharray = L;
    this.anim(h, [{ strokeDashoffset: L, opacity: 1 }, { strokeDashoffset: 0, opacity: 1, offset: .62 }, { strokeDashoffset: 0, opacity: 0 }], { duration: dur * 1.6, easing: 'ease-out' });
  }

  async splitLoop(t) {
    const E = this.els, amts = [40, 60, 100, 30], D = InvitationLanding.DEMO_SPLITS;
    let d = 0;
    for (let i = 0; i < 4; i++) { const c = E['chip' + i]; if (c) c.style.opacity = 0; }
    this.shown = 0; this.renderAmounts();
    await this.wait(300); if (!this.alive(t)) return;
    while (this.alive(t)) {
      let prev = null, total = 0;
      for (let i = 0; i < 4; i++) {
        if (!this.alive(t)) return;
        const c = E['chip' + i];
        if (prev) { prev.style.opacity = 0; this.anim(prev, [{ opacity: 1, transform: 'none' }, { opacity: 0, transform: 'translateY(-10px)' }], { duration: 250 }); }
        if (c) { c.style.opacity = 1; this.anim(c, [{ opacity: 0, transform: 'translateY(12px) scale(.92)' }, { opacity: 1, transform: 'none' }], { duration: 500, easing: 'cubic-bezier(.34,1.56,.64,1)' }); }
        prev = c;
        await this.wait(650); if (!this.alive(t)) return;
        if (E.coinB) E.coinB.style.opacity = 1;
        this.flow(E.hStem, 450);
        await this.travel(E.coinB, E.pStem, 450, t); if (!this.alive(t)) return;
        if (E.coinB) E.coinB.style.opacity = 0;
        [E.coinG, E.coinC].forEach(x => { if (x) x.style.opacity = 1; });
        this.flow(E.hG, 850); this.flow(E.hC, 850);
        await Promise.all([this.travel(E.coinG, E.hG, 850, t), this.travel(E.coinC, E.hC, 850, t)]); if (!this.alive(t)) return;
        [E.coinG, E.coinC].forEach(x => { if (x) x.style.opacity = 0; });
        const from = total; total += amts[i];
        this.tween(from, total, 700, v => { this.shown = v; this.renderAmounts(); }, t);
        this.pulse(E.bG);
        await this.wait(1000); if (!this.alive(t)) return;
        if (!this.touched && (i === 1 || i === 3)) {
          d = (d + 1) % D.length;
          await this.tween(this.split, D[d], 1000, v => { if (!this.touched) this.applySplit(v); }, t); if (!this.alive(t)) return;
        }
        await this.wait(500);
      }
      await this.wait(2200); if (!this.alive(t)) return;
      if (prev) { prev.style.opacity = 0; this.anim(prev, [{ opacity: 1 }, { opacity: 0 }], { duration: 300 }); }
      await this.tween(total, 0, 600, v => { this.shown = v; this.renderAmounts(); }, t);
      if (!this.alive(t)) return;
      await this.wait(700);
    }
  }

  setName(s) { this.setText(this.els.invName, s.toUpperCase()); if (this.curLayer === 'name') this.selectLayer('name'); }
  setColour(kd, k) {
    const E = this.els;
    if (E.invCard) { E.invCard.style.backgroundColor = kd.bg; E.invCard.style.color = kd.fg; }
    for (let i = 0; i < 3; i++) { const w = E['sw' + i]; if (w) w.style.boxShadow = i === k ? '0 0 0 2px #FFFFFF, 0 0 0 4px #0A2A40' : 'none'; }
    this.setText(E.invGift, kd.gift); this.setText(E.invCharity, kd.ch);
  }
  setAge(kd, animate) {
    const E = this.els;
    this.setText(E.invAge, String(kd.a));
    if (animate) this.anim(E.invAge, [{ transform: 'scale(.4) rotate(-20deg)', opacity: 0 }, { transform: 'none', opacity: 1 }], { duration: 600, easing: 'cubic-bezier(.34,1.56,.64,1)' });
  }
  applyKid(kd, k) { this.setColour(kd, k); this.setAge(kd, false); this.setName(kd.n); }
  selectLayer(k) {
    const E = this.els, card = E.invCard, sel = E.sel;
    if (!card || !sel) return;
    this.curLayer = k;
    const L = { name: ['Name', '48', E.tName], age: ['Age', '64', E.tAge], bg: ['Background', '—', null] }[k];
    const c = card.getBoundingClientRect();
    let x = 6, y = 6, w = c.width - 12, hh = c.height - 12;
    if (L[2]) { const r = L[2].getBoundingClientRect(); x = r.left - c.left - 6; y = r.top - c.top - 4; w = r.width + 12; hh = r.height + 8; }
    sel.style.left = x + 'px'; sel.style.top = y + 'px'; sel.style.width = w + 'px'; sel.style.height = hh + 'px'; sel.style.opacity = 1;
    this.setText(E.layer, L[0]); this.setText(E.size, L[1]);
    this.placeCaret();
  }
  placeCaret() {
    const E = this.els, cr = E.caret, card = E.invCard;
    if (!cr || !card) return;
    const tg = this.caretOn ? (this.curLayer === 'name' ? E.tName : this.curLayer === 'age' ? E.invAge : null) : null;
    if (!tg) { cr.style.display = 'none'; return; }
    const c = card.getBoundingClientRect(), r = tg.getBoundingClientRect();
    cr.style.display = 'block';
    cr.style.left = (r.right - c.left + 2) + 'px'; cr.style.top = (r.top - c.top + r.height * .08) + 'px'; cr.style.height = (r.height * .84) + 'px';
  }
  async typeLoop(t) {
    const K = InvitationLanding.KIDS;
    let k = 0;
    this.applyKid(K[0], 0);
    await this.wait(400); if (!this.alive(t)) return;
    this.selectLayer('name');
    while (this.alive(t)) {
      await this.wait(1400); if (!this.alive(t)) return;
      this.caretOn = true; this.placeCaret();
      await this.wait(500); if (!this.alive(t)) return;
      let s = K[k].n;
      while (s.length) { s = s.slice(0, -1); this.setName(s); await this.wait(70); if (!this.alive(t)) return; }
      k = (k + 1) % K.length;
      await this.wait(300); if (!this.alive(t)) return;
      for (const ch of K[k].n) { s += ch; this.setName(s); await this.wait(140); if (!this.alive(t)) return; }
      this.caretOn = false;
      await this.wait(1000); if (!this.alive(t)) return;
      this.selectLayer('age');
      await this.wait(600); if (!this.alive(t)) return;
      this.caretOn = true; this.setAge(K[k], true); this.placeCaret();
      await this.wait(1100); if (!this.alive(t)) return;
      this.caretOn = false; this.selectLayer('bg');
      await this.wait(700); if (!this.alive(t)) return;
      this.setColour(K[k], k);
      await this.wait(1800); if (!this.alive(t)) return;
      this.selectLayer('name');
    }
  }

  async feedLoop(t) {
    const E = this.els, amts = [40, 30, 100, 60], goal = 400;
    while (this.alive(t)) {
      for (let i = 0; i < 4; i++) { const r = E['rsvp' + i]; if (r) { r.style.opacity = 0; r.style.transform = 'translateX(18px)'; } }
      this.setText(E.feedRaised, '$0'); this.setText(E.feedCount, '0 replies');
      if (E.feedBar) { E.feedBar.style.transition = 'width .5s ease'; E.feedBar.style.width = '0%'; }
      let tot = 0;
      for (let i = 0; i < 4; i++) {
        await this.wait(i === 0 ? 900 : 1600); if (!this.alive(t)) return;
        const r = E['rsvp' + i];
        if (r) { r.style.opacity = 1; r.style.transform = 'none'; this.anim(r, [{ opacity: 0, transform: 'translateX(18px)' }, { opacity: 1, transform: 'none' }], { duration: 520, easing: 'cubic-bezier(.2,.9,.3,1.2)' }); }
        this.count(E.feedRaised, tot, tot + amts[i], 700, t); tot += amts[i];
        this.setText(E.feedCount, (i + 1) + (i === 0 ? ' reply' : ' replies'));
        if (E.feedBar) { E.feedBar.style.transition = 'width .8s cubic-bezier(.65,0,.35,1)'; E.feedBar.style.width = (tot / goal * 100) + '%'; }
      }
      await this.wait(3200); if (!this.alive(t)) return;
      for (let i = 0; i < 4; i++) { const r = E['rsvp' + i]; if (r) { r.style.opacity = 0; this.anim(r, [{ opacity: 1 }, { opacity: 0 }], { duration: 350, delay: i * 60, fill: 'backwards' }); } }
      await this.wait(800);
    }
  }

  setVibe(v) {
    for (let i = 0; i < 5; i++) {
      const el = this.els['vibe' + i]; if (!el) continue;
      const on = i === v;
      el.style.backgroundColor = on ? '#0A2A40' : '#FFFFFF';
      el.style.color = on ? '#FFFFFF' : '#0A2A40';
      el.style.borderColor = on ? '#0A2A40' : '#E3E8EC';
    }
  }
  async aiLoop(t) {
    const E = this.els, P = InvitationLanding.PROMPTS;
    let i = 0;
    await this.wait(1200);
    while (this.alive(t)) {
      let s = E.aiPrompt ? E.aiPrompt.textContent : '';
      while (s.length) { s = s.slice(0, -3); this.setText(E.aiPrompt, s); await this.wait(16); if (!this.alive(t)) return; }
      this.setVibe(P[i].v);
      await this.wait(250); if (!this.alive(t)) return;
      for (const ch of P[i].p) { s += ch; this.setText(E.aiPrompt, s); await this.wait(34 + Math.random() * 40); if (!this.alive(t)) return; }
      await this.wait(450); if (!this.alive(t)) return; this.pulse(E.aiBtn);
      await this.wait(2800); if (!this.alive(t)) return;
      i = (i + 1) % P.length;
    }
  }

  stickyInit(t) {
    const E = this.els, el = E.sticky;
    if (!el) return;
    el.style.transform = 'translateY(160%)';
    el.setAttribute('aria-hidden', 'true');
    el.inert = true;
    const st = { hero: true, past: false, fin: false };
    const upd = () => {
      if (!this.alive(t)) return;
      const show = window.innerWidth < 760 && !st.hero && st.past && !st.fin;
      el.style.transform = show ? 'translateY(0)' : 'translateY(160%)';
      el.setAttribute('aria-hidden', String(!show));
      el.inert = !show;
    };
    if (window.IntersectionObserver) {
      const o = new IntersectionObserver(es => {
        es.forEach(e => {
          if (e.target === E.heroCta) { st.hero = e.isIntersecting; st.past = e.boundingClientRect.top < 0; }
          else if (e.target === E.finalCta) st.fin = e.isIntersecting;
        });
        upd();
      });
      if (E.heroCta) o.observe(E.heroCta);
      if (E.finalCta) o.observe(E.finalCta);
      this.obs.push(o);
    }
    this._onResize = upd;
    window.addEventListener('resize', upd);
  }

  restoreDemo() {
    const E = this.els;
    this.setText(E.heroRaised, '$240');
    if (E.heroBar) E.heroBar.style.width = '57%';
    this.setText(E.feedRaised, '$230'); this.setText(E.feedCount, '4 replies');
    if (E.feedBar) E.feedBar.style.width = '57%';
    for (let i = 0; i < 4; i++) {
      if (E['rsvp' + i]) { E['rsvp' + i].style.opacity = 1; E['rsvp' + i].style.transform = 'none'; }
      if (E['chip' + i]) { E['chip' + i].style.opacity = i === 0 ? 1 : 0; E['chip' + i].style.transform = 'none'; }
    }
    ['coinB', 'coinG', 'coinC', 'hStem', 'hG', 'hC'].forEach(key => { if (E[key]) E[key].style.opacity = 0; });
    this.setText(E.aiPrompt, InvitationLanding.PROMPTS[0].p); this.setVibe(0);
    if (E.aiCaret) E.aiCaret.style.display = this.reduced ? 'none' : '';
  }
}

let landing;
function connectLanding() {
  const element = document.getElementById('invitation-landing');
  if (!element || landing) return;
  landing = new InvitationLanding(element);
}
function disconnectLanding() { landing?.destroy(); landing = null; }
if (typeof document !== 'undefined') {
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', connectLanding, { once: true });
  else connectLanding();
  window.addEventListener('pagehide', disconnectLanding);
  window.addEventListener('pageshow', connectLanding);
}
